/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import { RequestStatus } from '../../data/constants';

const COMPANY_CACHED_DATA_EMPTY = {
  allReflections: [],
  assessments: [],
  assessmentsV2: [],
  completionBySkill: [],
  skillsDashboardAssignmentType: [],
  skillsDashboardAssignment: [],
  skillsDashboard: [],
  totalLearningOverview: [],
  unregisteredLearners: [],
};

const createCompanyCachedDataEntry = () => ({
  data: { ...COMPANY_CACHED_DATA_EMPTY },
  pages: {},
  manifests: {},
  endpointStatus: {},
  loadingStatus: RequestStatus.CLEAR,
  hydrated: false,
  activeRequestId: null,
  error: null,
});

const ensureCompanyCachedDataEntry = (state, companyName) => {
  if (!state.companyCachedDataByOrg[companyName]) {
    state.companyCachedDataByOrg[companyName] = createCompanyCachedDataEntry();
  }

  return state.companyCachedDataByOrg[companyName];
};

const flattenEndpointPages = (endpointPages) => Object.keys(endpointPages)
  .map((pageNumber) => Number(pageNumber))
  .filter((pageNumber) => Number.isInteger(pageNumber) && pageNumber > 0)
  .sort((a, b) => a - b)
  .flatMap((pageNumber) => {
    const pageData = endpointPages[pageNumber];
    return Array.isArray(pageData) ? pageData : [];
  });

const slice = createSlice({
  name: 'studioHome',
  initialState: {
    loadingStatuses: {
      studioHomeLoadingStatus: RequestStatus.IN_PROGRESS,
      courseNotificationLoadingStatus: RequestStatus.IN_PROGRESS,
      courseLoadingStatus: RequestStatus.IN_PROGRESS,
      libraryLoadingStatus: RequestStatus.IN_PROGRESS,
      companyManagementPortalLoadingStatus: RequestStatus.IN_PROGRESS,
      platformLoadingStatus: '',
      programLoadingStatus: '',
      fullCourseListLoadingStatus: RequestStatus.IN_PROGRESS,
    },
    savingStatuses: {
      courseCreatorSavingStatus: '',
      deleteNotificationSavingStatus: '',
      createCompanySavingStatus: '',
      updateCompanyFriendlyNameSavingStatus: '',
      updateCompanySizeSavingStatus: '',
      updateCompanyWebsiteSavingStatus: '',
      updateCustomCsvFieldsSavingStatus: '',
      updateCustomAdminPortalFeaturesSavingStatus: '',
      manageCompanyAdminsSavingStatus: '',
      fetchCompanyLicensesSavingStatus: '',
      addLicensesSavingStatus: '',
      removeLicensesSavingStatus: '',
      updateLicensesSavingStatus: '',
    },
    studioHomeData: {},
    companyManagement: [],
    selectedCompanyDetails: null,
    companyCatalog: null,
    legacyLicenses: null,
    platforms: [],
    programs: [],
    selectedCompanyOrPlatformKey: null,
    fullCourseList: [],
    companyCachedData: {},
    companyCachedDataByOrg: {},
    companyEmployees: {},
    companyEmployeeCount: 0,
    companyEmployeePage: 1,
    companyEmployeePagesByOrg: {},
    companyAdmins: [],
    // Per-org, per-username enrollment enrichment (courses + certificate/grade/progress),
    // fetched in small background batches from v1/dashboard/enrollments/bulk/.
    learnerEnrollmentsByOrg: {},
    // Prevent results from an in-flight batch from restoring a learner removed from the roster.
    removedLearnerEnrollmentsByOrg: {},
    studioHomeCoursesRequestParams: {
      currentPage: 1,
      search: undefined,
      order: 'display_name',
      archivedOnly: undefined,
      activeOnly: undefined,
      isFiltered: false,
      cleanFilters: false,
    },
  },
  reducers: {
    updateLoadingStatuses: (state, { payload }) => {
      state.loadingStatuses = { ...state.loadingStatuses, ...payload };
    },
    updateSavingStatuses: (state, { payload }) => {
      state.savingStatuses = { ...state.savingStatuses, ...payload };
    },
    fetchStudioHomeDataSuccess: (state, { payload }) => {
      Object.assign(state.studioHomeData, payload);
    },
    fetchCourseDataSuccess: (state, { payload }) => {
      const { courses, archivedCourses, inProcessCourseActions } = payload;
      state.studioHomeData.courses = courses;
      state.studioHomeData.archivedCourses = archivedCourses;
      state.studioHomeData.inProcessCourseActions = inProcessCourseActions;
    },
    fetchCourseDataSuccessV2: (state, { payload }) => {
      const { courses, archivedCourses = [], inProcessCourseActions } = payload.results;
      const { numPages, count } = payload;
      state.studioHomeData.courses = courses;
      state.studioHomeData.archivedCourses = archivedCourses;
      state.studioHomeData.inProcessCourseActions = inProcessCourseActions;
      state.studioHomeData.numPages = numPages;
      state.studioHomeData.coursesCount = count;
    },
    fetchLibraryDataSuccess: (state, { payload }) => {
      const { libraries } = payload;
      state.studioHomeData.libraries = libraries;
    },
    fetchFullCourseListSuccess: (state, { payload }) => {
      state.fullCourseList = payload;
    },
    fetchCompanyManagementPortalDataSuccess: (state, { payload }) => {
      state.companyManagement = payload;
    },
    fetchCompanyManagementSpecificDataSuccess: (state, { payload }) => {
      state.selectedCompanyDetails = payload;
    },
    fetchCompanyCatalogSuccess: (state, { payload }) => {
      state.companyCatalog = payload;
    },
    fetchLegacyLicensesSuccess: (state, { payload }) => {
      state.legacyLicenses = payload;
    },
    fetchPlatformDataSuccess: (state, { payload }) => {
      state.platforms = payload;
    },
    createPlatformSuccess: (state, { payload }) => {
      state.platforms.push(payload);
    },
    updatePlatformSuccess: (state, { payload }) => {
      const index = state.platforms.findIndex(p => p.key === payload.key);
      if (index !== -1) {
        state.platforms[index] = payload;
      }
    },
    fetchProgramDataSuccess: (state, { payload }) => {
      state.programs = payload;
    },
    createProgramSuccess: (state, { payload }) => {
      state.programs.push(payload);
    },
    updateProgramSuccess: (state, { payload }) => {
      const index = state.programs.findIndex(p => p.item_id === payload.item_id);
      if (index !== -1) {
        state.programs[index] = payload;
      }
    },
    deleteProgramSuccess: (state, { payload }) => {
      state.programs = state.programs.filter(p => p.item_id !== payload.item_id);
    },
    updateStudioHomeCoursesCustomParams: (state, { payload }) => {
      Object.assign(state.studioHomeCoursesRequestParams, payload);
    },
    setSelectedCompanyOrPlatformKey: (state, { payload }) => {
      state.selectedCompanyOrPlatformKey = payload;
      if (!payload) {
        state.companyCachedData = {};
        return;
      }

      state.companyCachedData = state.companyCachedDataByOrg[payload]?.data || {};
    },
    initializeCompanyCachedData: (state, { payload }) => {
      const { companyName, requestId } = payload;
      const entry = ensureCompanyCachedDataEntry(state, companyName);
      entry.loadingStatus = RequestStatus.IN_PROGRESS;
      entry.activeRequestId = requestId;
      entry.error = null;
    },
    storeCompanyCachedDataManifest: (state, { payload }) => {
      const {
        companyName,
        endpoint,
        manifest,
        requestId,
      } = payload;
      const entry = ensureCompanyCachedDataEntry(state, companyName);
      if (entry.activeRequestId !== requestId) {
        return;
      }
      entry.manifests[endpoint] = manifest;
    },
    storeCompanyCachedDataPage: (state, { payload }) => {
      const {
        companyName,
        endpoint,
        pageNumber,
        data,
        requestId,
      } = payload;
      const entry = ensureCompanyCachedDataEntry(state, companyName);
      if (entry.activeRequestId !== requestId) {
        return;
      }

      if (!entry.pages[endpoint]) {
        entry.pages[endpoint] = {};
      }

      entry.pages[endpoint][pageNumber] = Array.isArray(data) ? data : [];
      entry.data[endpoint] = flattenEndpointPages(entry.pages[endpoint]);
    },
    storeCompanyCachedDataValue: (state, { payload }) => {
      const {
        companyName,
        endpoint,
        data,
        manifest,
        requestId,
      } = payload;
      const entry = ensureCompanyCachedDataEntry(state, companyName);
      if (entry.activeRequestId !== requestId) {
        return;
      }

      if (manifest !== undefined) {
        entry.manifests[endpoint] = manifest;
      }
      entry.data[endpoint] = Array.isArray(data) ? data : (data ?? []);
    },
    updateCompanyCachedDataEndpointStatus: (state, { payload }) => {
      const {
        companyName,
        endpoint,
        status,
        requestId,
      } = payload;
      const entry = ensureCompanyCachedDataEntry(state, companyName);
      if (entry.activeRequestId !== requestId) {
        return;
      }
      entry.endpointStatus[endpoint] = status;
    },
    fetchSpecificCompanyCachedDataSuccess: (state, { payload }) => {
      const {
        companyName,
        requestId,
        hydrated = true,
      } = payload;
      const entry = ensureCompanyCachedDataEntry(state, companyName);
      if (entry.activeRequestId !== requestId) {
        return;
      }
      entry.loadingStatus = hydrated ? RequestStatus.SUCCESSFUL : RequestStatus.IN_PROGRESS;
      entry.hydrated = hydrated;
      entry.error = null;
      if (state.selectedCompanyOrPlatformKey === companyName) {
        state.companyCachedData = entry.data;
      }
    },
    fetchSpecificCompanyCachedDataFailure: (state, { payload }) => {
      const {
        companyName,
        requestId,
        error,
      } = payload;
      const entry = ensureCompanyCachedDataEntry(state, companyName);
      if (entry.activeRequestId !== requestId) {
        return;
      }
      entry.loadingStatus = RequestStatus.FAILED;
      entry.error = error;
      if (state.selectedCompanyOrPlatformKey === companyName) {
        state.companyCachedData = entry.data;
      }
    },
    syncSelectedCompanyCachedData: (state, { payload }) => {
      const companyName = payload;
      if (!companyName) {
        state.companyCachedData = {};
        return;
      }
      if (state.selectedCompanyOrPlatformKey !== companyName) {
        return;
      }
      const entry = state.companyCachedDataByOrg[companyName];
      state.companyCachedData = entry?.data || {};
    },
    fetchCompanyEmployeesSuccess: (state, { payload }) => {
      state.companyEmployees = payload;
    },
    fetchCompanyEmployeePageSuccess: (state, { payload }) => {
      const {
        companyName, count, page, pageSize, nextPage, results,
      } = payload;
      if (companyName) {
        const entry = state.companyEmployeePagesByOrg[companyName] || { pages: {} };
        entry.count = count;
        entry.pageSize = pageSize;
        entry.pages[page] = { nextPage, results };
        state.companyEmployeePagesByOrg[companyName] = entry;
      }
      state.companyEmployees = results;
      state.companyEmployeeCount = count;
      state.companyEmployeePage = page;
    },
    storeCompanyEmployeePage: (state, { payload }) => {
      const {
        companyName, count, page, pageSize, nextPage, results,
      } = payload;
      const entry = state.companyEmployeePagesByOrg[companyName] || { pages: {} };
      entry.count = count;
      entry.pageSize = pageSize;
      entry.pages[page] = { nextPage, results };
      state.companyEmployeePagesByOrg[companyName] = entry;
    },
    clearCompanyEmployeePages: (state, { payload }) => {
      delete state.companyEmployeePagesByOrg[payload];
    },
    fetchCompanyAdminsSuccess: (state, { payload }) => {
      state.companyAdmins = payload;
    },
    appendCompanyAdmins: (state, { payload }) => {
      const knownUsernames = new Set(state.companyAdmins.map((admin) => admin.username));
      state.companyAdmins.push(...payload.filter((admin) => !knownUsernames.has(admin.username)));
    },
    pruneLearnerEnrollmentsForOrg: (state, { payload }) => {
      const { companyName, usernames } = payload;
      const activeUsernames = new Set(usernames);
      const orgEntry = state.learnerEnrollmentsByOrg[companyName] || {};
      const removedEntry = state.removedLearnerEnrollmentsByOrg[companyName] || {};

      Object.keys(orgEntry).forEach((username) => {
        if (!activeUsernames.has(username)) {
          delete orgEntry[username];
          removedEntry[username] = true;
        }
      });

      state.removedLearnerEnrollmentsByOrg[companyName] = removedEntry;
    },
    invalidateLearnerEnrollments: (state, { payload }) => {
      const { companyName, usernames } = payload;
      const orgEntry = state.learnerEnrollmentsByOrg[companyName] || {};
      usernames.forEach((username) => {
        delete orgEntry[username];
      });
      state.learnerEnrollmentsByOrg[companyName] = orgEntry;
    },
    initializeLearnerEnrollmentsForOrg: (state, { payload }) => {
      const companyName = payload;
      if (!state.learnerEnrollmentsByOrg[companyName]) {
        state.learnerEnrollmentsByOrg[companyName] = {};
      }
    },
    setLearnerEnrollmentBatchLoading: (state, { payload }) => {
      const { companyName, usernames, include = [] } = payload;
      const isProgressRequest = include.includes('progress');
      if (!state.learnerEnrollmentsByOrg[companyName]) {
        state.learnerEnrollmentsByOrg[companyName] = {};
      }
      const orgEntry = state.learnerEnrollmentsByOrg[companyName];
      const removedEntry = state.removedLearnerEnrollmentsByOrg[companyName] || {};
      usernames.forEach((username) => {
        delete removedEntry[username];
        const existing = orgEntry[username];
        orgEntry[username] = {
          courses: existing?.courses ?? null,
          loadingStatus: isProgressRequest
            ? existing?.loadingStatus ?? RequestStatus.CLEAR
            : RequestStatus.IN_PROGRESS,
          progressLoadingStatus: isProgressRequest
            ? RequestStatus.IN_PROGRESS
            : existing?.progressLoadingStatus ?? RequestStatus.CLEAR,
          error: null,
        };
      });
      state.removedLearnerEnrollmentsByOrg[companyName] = removedEntry;
    },
    storeLearnerEnrollmentBatchSuccess: (state, { payload }) => {
      const {
        companyName, results, include = [], identifiers = [],
      } = payload;
      const isProgressRequest = include.includes('progress');
      if (!state.learnerEnrollmentsByOrg[companyName]) {
        state.learnerEnrollmentsByOrg[companyName] = {};
      }
      const orgEntry = state.learnerEnrollmentsByOrg[companyName];
      const removedEntry = state.removedLearnerEnrollmentsByOrg[companyName] || {};
      const usernamesByEmail = new Map(identifiers.map(({ email, username }) => [email, username]));
      results.forEach((result) => {
        const username = result?.user?.username
          ?? result?.input?.username
          ?? usernamesByEmail.get(result?.user?.email ?? result?.input?.email);
        if (!username || removedEntry[username]) {
          return;
        }
        const existing = orgEntry[username];
        const summaryCourses = new Map(
          (existing?.courses || []).map((course) => [course.course_id, course]),
        );
        const courses = isProgressRequest
          ? (result.courses || []).map((course) => {
            const summaryCourse = summaryCourses.get(course.course_id) || {};
            summaryCourses.delete(course.course_id);
            return {
              ...summaryCourse,
              ...course,
              progress: { ...summaryCourse.progress, ...course.progress },
            };
          })
          : result.courses ?? [];
        orgEntry[username] = {
          courses: isProgressRequest ? [...summaryCourses.values(), ...courses] : courses,
          loadingStatus: isProgressRequest
            ? existing?.loadingStatus ?? RequestStatus.SUCCESSFUL
            : RequestStatus.SUCCESSFUL,
          progressLoadingStatus: isProgressRequest
            ? RequestStatus.SUCCESSFUL
            : existing?.progressLoadingStatus ?? RequestStatus.CLEAR,
          error: result.error ?? null,
        };
      });
    },
    storeLearnerEnrollmentBatchFailure: (state, { payload }) => {
      const {
        companyName, usernames, error, include = [],
      } = payload;
      const isProgressRequest = include.includes('progress');
      if (!state.learnerEnrollmentsByOrg[companyName]) {
        state.learnerEnrollmentsByOrg[companyName] = {};
      }
      const orgEntry = state.learnerEnrollmentsByOrg[companyName];
      usernames.forEach((username) => {
        orgEntry[username] = {
          courses: orgEntry[username]?.courses ?? null,
          loadingStatus: isProgressRequest
            ? orgEntry[username]?.loadingStatus ?? RequestStatus.CLEAR
            : RequestStatus.FAILED,
          progressLoadingStatus: isProgressRequest
            ? RequestStatus.FAILED
            : orgEntry[username]?.progressLoadingStatus ?? RequestStatus.CLEAR,
          error,
        };
      });
    },
  },
});

export const {
  updateSavingStatuses,
  updateLoadingStatuses,
  fetchStudioHomeDataSuccess,
  fetchCourseDataSuccess,
  fetchCourseDataSuccessV2,
  fetchLibraryDataSuccess,
  fetchFullCourseListSuccess,
  fetchCompanyManagementPortalDataSuccess,
  fetchCompanyManagementSpecificDataSuccess,
  fetchCompanyCatalogSuccess,
  fetchLegacyLicensesSuccess,
  fetchPlatformDataSuccess,
  createPlatformSuccess,
  updatePlatformSuccess,
  fetchProgramDataSuccess,
  createProgramSuccess,
  updateProgramSuccess,
  deleteProgramSuccess,
  updateStudioHomeCoursesCustomParams,
  setSelectedCompanyOrPlatformKey,
  initializeCompanyCachedData,
  storeCompanyCachedDataManifest,
  storeCompanyCachedDataPage,
  storeCompanyCachedDataValue,
  updateCompanyCachedDataEndpointStatus,
  fetchSpecificCompanyCachedDataFailure,
  fetchSpecificCompanyCachedDataSuccess,
  syncSelectedCompanyCachedData,
  fetchCompanyEmployeesSuccess,
  fetchCompanyEmployeePageSuccess,
  storeCompanyEmployeePage,
  clearCompanyEmployeePages,
  fetchCompanyAdminsSuccess,
  appendCompanyAdmins,
  pruneLearnerEnrollmentsForOrg,
  invalidateLearnerEnrollments,
  initializeLearnerEnrollmentsForOrg,
  setLearnerEnrollmentBatchLoading,
  storeLearnerEnrollmentBatchSuccess,
  storeLearnerEnrollmentBatchFailure,
} = slice.actions;

export const {
  reducer,
} = slice;
